import { useQueryClient } from '@tanstack/react-query';
import { LogOut, Menu, UserCircle, type LucideIcon } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { LegalLinks } from '@/features/legal/legal-links';
import { api } from '@/lib/api';
import { applyTheme, clearTheme, useTheme } from '@/lib/theme';
import { cn } from '@/lib/utils';

/** Peças compartilhadas pelo layout do tenant (AppLayout) e do painel da plataforma (PlatformLayout). */

export interface ShellNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}
export interface ShellNavGroup {
  label: string;
  items: ShellNavItem[];
}

export function Brand({ to }: { to: string }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-2.5 rounded-md px-2 py-1 font-display text-lg font-semibold tracking-tight text-white"
    >
      <img src="/favicon.svg" alt="" className="size-7" />
      BarbeiroPro
    </Link>
  );
}

export function SidebarNav({
  groups,
  rootTo,
  ariaLabel = 'Principal',
  onNavigate,
}: {
  groups: ShellNavGroup[];
  /** rota raiz da área: só fica ativa com correspondência exata */
  rootTo: string;
  ariaLabel?: string;
  onNavigate?: () => void;
}) {
  return (
    <nav aria-label={ariaLabel} className="flex flex-col gap-5">
      {groups.map((g) => {
        if (!g.items.length) return null;
        return (
          <div key={g.label} className="flex flex-col gap-0.5">
            <p className="px-3 pb-1 text-xs font-medium text-sidebar-muted">{g.label}</p>
            {g.items.map((i) => (
              <NavLink
                key={i.to}
                to={i.to}
                end={i.to === rootTo}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    'group flex min-h-10 items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-sidebar-accent text-white'
                      : 'text-sidebar-foreground/85 hover:bg-sidebar-accent/60 hover:text-white',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <i.icon
                      className={cn(
                        'size-[18px] shrink-0',
                        isActive ? 'text-brass' : 'text-sidebar-muted group-hover:text-brass',
                      )}
                      aria-hidden
                    />
                    {i.label}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        );
      })}
    </nav>
  );
}

/** Bloco "Minha conta" do rodapé da sidebar. Sem `to`, mostra só o e-mail (sem link). */
export function AccountLink({
  to,
  email,
  label = 'Minha conta',
  onNavigate,
}: {
  to?: string;
  email: string;
  label?: string;
  onNavigate?: () => void;
}) {
  const body = (
    <>
      <UserCircle className="size-[18px] shrink-0 text-sidebar-muted" aria-hidden />
      <span className="min-w-0">
        <span className="block">{label}</span>
        <span className="block truncate text-xs font-normal text-sidebar-muted">{email}</span>
      </span>
    </>
  );
  const base =
    'mt-auto flex min-h-12 items-center gap-3 rounded-md border-t border-white/10 px-3 pt-2 text-sm font-medium transition-colors';
  if (!to) return <div className={cn(base, 'text-sidebar-foreground/85')}>{body}</div>;
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(base, isActive ? 'text-white' : 'text-sidebar-foreground/85 hover:text-white')
      }
    >
      {body}
    </NavLink>
  );
}

/** Menu do usuário: avatar + nome, cabeçalho com nome/e-mail/papel e os itens passados em `children`. */
export function UserMenuShell({
  name,
  email,
  roleLine,
  children,
}: {
  name: string;
  email: string;
  roleLine?: string | null;
  /** itens entre o cabeçalho e o "Sair" */
  children?: ReactNode;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const initials = name
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="gap-2.5 px-1.5 sm:px-2" aria-label="Menu do usuário">
          <span className="flex size-8 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
            {initials}
          </span>
          <span className="hidden max-w-40 truncate text-sm sm:block">{name}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 max-w-[calc(100vw-1rem)]">
        <div className="px-3 py-2">
          <p className="truncate text-sm font-semibold">{name}</p>
          <p className="truncate text-xs text-muted-foreground">{email}</p>
          {roleLine && <p className="mt-0.5 truncate text-xs text-muted-foreground">{roleLine}</p>}
        </div>
        <DropdownMenuSeparator />
        {children}
        <DropdownMenuItem
          onSelect={async () => {
            await api.post('/auth/logout').catch(() => undefined);
            qc.clear();
            navigate('/entrar', { replace: true });
          }}
        >
          <LogOut className="size-4" aria-hidden /> Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Aplica o tema escolhido enquanto a área logada está aberta e acompanha o sistema no modo "Do sistema". */
export function useAppTheme() {
  const theme = useTheme((s) => s.theme);
  useEffect(() => {
    applyTheme(theme);
    if (theme !== 'system') return clearOnLeave;
    const mq = matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    mq.addEventListener('change', onChange);
    return () => {
      mq.removeEventListener('change', onChange);
      clearTheme();
    };
  }, [theme]);
}
const clearOnLeave = () => clearTheme();

/** Grid sidebar + drawer mobile + header + `<main>`; o conteúdo de cada região vem do layout. */
export function AppShell({
  brandTo,
  renderNav,
  renderAccount,
  headerStart,
  headerEnd,
  banner,
  afterHeader,
  children,
}: {
  brandTo: string;
  renderNav: (onNavigate?: () => void) => ReactNode;
  renderAccount: (onNavigate?: () => void) => ReactNode;
  headerStart: ReactNode;
  headerEnd: ReactNode;
  /** faixa acima de tudo (impersonação) */
  banner?: ReactNode;
  /** logo abaixo do header (aviso de assinatura) */
  afterHeader?: ReactNode;
  children: ReactNode;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const close = () => setMobileOpen(false);
  return (
    <>
      {banner}
      <div className="min-h-dvh lg:grid lg:grid-cols-[16rem_minmax(0,1fr)]">
        <a
          href="#conteudo"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded focus:bg-card focus:px-3 focus:py-2"
        >
          Pular para o conteúdo
        </a>
        <aside className="sticky top-[var(--imp-h,0px)] hidden h-[calc(100dvh-var(--imp-h,0px))] flex-col gap-6 overflow-y-auto bg-sidebar px-3 py-5 lg:flex">
          <Brand to={brandTo} />
          {renderNav()}
          {renderAccount()}
          <LegalLinks className="px-3 text-sidebar-muted" linkClassName="hover:text-white" />
        </aside>
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent
            side="left"
            className="w-72 border-0 bg-sidebar p-0 px-3 py-5 [&>button]:text-sidebar-foreground"
          >
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <div className="flex h-full flex-col gap-6">
              <Brand to={brandTo} />
              {renderNav(close)}
              {renderAccount(close)}
              <LegalLinks className="px-3 pb-1 text-sidebar-muted" linkClassName="hover:text-white" onNavigate={close} />
            </div>
          </SheetContent>
        </Sheet>
        <div className="flex min-w-0 flex-col">
          <header className="sticky top-[var(--imp-h,0px)] z-30 flex h-16 items-center gap-2 border-b bg-background/90 px-3 backdrop-blur sm:px-6">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              aria-label="Abrir menu"
              onClick={() => setMobileOpen(true)}
            >
              <Menu className="size-5" />
            </Button>
            {headerStart}
            <div className="ml-auto" />
            {headerEnd}
          </header>
          {afterHeader}
          <main id="conteudo" className="mx-auto w-full max-w-[84rem] flex-1 px-4 py-6 sm:px-8 sm:py-8">
            {children}
          </main>
        </div>
      </div>
    </>
  );
}
